import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { contactsApi } from '@/api/services/contacts.api';
import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSION } from '@/features/users-roles/constants';
import { showBulkJobToast } from '../components/show-bulk-job-toast';
import { bulkJobKindFromAction } from '../utils/bulk-job-copy';
import { isTerminalBulkJob } from './use-bulk-job';

/**
 * A background bulk job outlives the page that started it. After a reload (or opening the
 * app in a new tab) its progress toast is gone, and the only signal left was a 409 on the
 * next bulk action. Once per app load, re-show a progress toast for every job still running.
 */
export function useResumeBulkJobToasts(): void {
    const { hasPermission } = usePermissions();
    const canRead = hasPermission(PERMISSION.CONTACTS_READ);
    const { data: jobs } = useQuery({
        queryKey: ['contact-bulk-jobs', 'resume'],
        queryFn: contactsApi.listBulkJobs,
        enabled: canRead,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        retry: false,
    });

    useEffect(() => {
        for (const job of jobs ?? []) {
            if (isTerminalBulkJob(job.status)) continue;
            showBulkJobToast(job.id, job.total, bulkJobKindFromAction(job.action));
        }
    }, [jobs]);
}
